import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StarIcon from '../../../assets/star-icon.svg';
import { PATHS } from '../../../constants/Paths.constants';
import { MAX_BULK_DELETE_AI_SYSTEMS } from '../../../constants/AISystems.constants';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { useOwners } from '../../../hooks/useOwners';
import {
  continueAiSystemDraft,
  bulkDeleteAiSystems,
  fetchAISystems,
  fetchAiSystemAccounts,
  fetchAiSystemFilterOptions,
  fetchOrganizations,
  startAiSystem
} from '../../../services/AISystemsService';
import {
  dedupeRegulationsByCode,
  fetchRegulations
} from '../../../services/RegulationsService';
import { getAIClassifications } from '../../../services/AIClassificationService';
import {
  selectIsPlatformAdmin,
  selectIsSuperAdmin
} from '../../../store/selectors/authSelectors';
import {
  appendSystemsSuccess,
  fetchSystemsFailure,
  fetchSystemsStart,
  fetchSystemsSuccess,
  resetFilters,
  setAccountFilter,
  setComplianceFilter,
  setEntityFilter,
  setOffset,
  setOrganizationFilter,
  setOwnersFilter,
  setRegulationFilter,
  setRiskFilter,
  setSearch,
  setSorting
} from '../../../store/slices/aiSystemsSlice';
import {
  setAiSystemId,
  setEnableStepper,
  setIsDraftLoaded,
  setPreStepPart,
  setPreviousQuestions,
  setQuestionnaireId,
  setShowPreStep,
  setStartAiSystemBodyResponse,
  setStartAiSystemBodyRequest,
  updateStepBasedOnBackend
} from '../../../store/slices/stepperSlice';
import {
  fetchClassificationsFailure,
  fetchClassificationsStart,
  fetchClassificationsSuccess,
  resetAIPowered
} from '../../../store/slices/aiPoweredSlice';
import { fetchSummaryData } from '../../../store/slices/summarySlice';
import { buildAISystemsQuery } from '../../../utils/AiSystems.util';
import {
  AiSystemFilterOptions,
  BulkDeleteAISystemsRequest,
  Owner,
  StartAiSystemRequest
} from '../../../types/AISystems.types';
import { mapSummaryToStatsSection } from '../../../utils/SummaryMapping.util';
import { AISystemsTable } from '../../tables/AISystemsTable/AISystemsTable';
import { EmptyState } from '../../ui/EmptyState/EmptyState';
import Loader from '../../ui/Loader/Loader';
import { FiltersSection } from '../FilterSection/FiltersSection';
import { SearchSection } from '../SearchSection/SearchSection';
import { StatsSection } from '../StatsSection/StatsSection';
import { setReportData } from '../../../store/slices/reportSlice';
import {
  initialDocumentationParams,
  setDocumentationReqBody,
  setDocuments
} from '../../../store/slices/documentationSlice';
import { resetSystemLogging } from '../../../store/slices/systemLoggingSlice';
import { resetRepository } from '../../../store/slices/repositorySlice';
import { AddAISystemAction } from '../AddAISystemAction/AddAISystemAction';
import { TextSecondaryButton } from '../../ui/Button/Buttons';
import { showToast } from '../../../store/slices/toastSlice';
import ModalDeleteConfirmation from '../../ui/Modal/ModalDeleteConfirmation';

export function AISystemsOverview() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { systems, total, loading, filters, pagination, sorting } =
    useAppSelector((state) => state.aiSystems);
  const { data: summaryData } = useAppSelector((state) => state.summary);
  const { user } = useAppSelector((state) => state.auth);

  const isAdmin = useAppSelector(selectIsPlatformAdmin);

  const { owners, loading: ownersLoading } = useOwners();

  const [selectedSystemIds, setSelectedSystemIds] = useState<string[]>([]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const deletingRef = useRef(false);
  const [organizationOptions, setOrganizationOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);
  const [accountOptions, setAccountOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [regulationOptions, setRegulationOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [filterOptions, setFilterOptions] = useState<AiSystemFilterOptions | null>(
    null
  );
  // Gates the summary/list fetches and the render until the default regulation
  // has been selected, so no unscoped request fires and the onboarding empty
  // state cannot flash before scoping is applied.
  const [regulationsReady, setRegulationsReady] = useState(false);
  // Mount-time regulation code — read by the mount-only regulations-load effect
  // to preserve a valid existing selection without adding filters.regulationCode
  // to its dependency array.
  const regulationCodeRef = useRef(filters.regulationCode);
  const hasMore = systems.length < total;
  const isSuperAdmin = useAppSelector(selectIsSuperAdmin);
  const allSystemsSeleceted = isAdmin
    ? selectedSystemIds.length === systems.length
    : selectedSystemIds.length ===
      systems.filter((system) =>
        system.owner.id === system.createdById
          ? user?.email === system.owner.email
          : false
      ).length;

  const isOneSelectedSystem = selectedSystemIds.length === 1;

  useEffect(() => {
    setSelectedSystemIds([]);
  }, [filters, sorting, pagination.limit, user?.id]);

  useEffect(() => {
    const loadedIds = new Set(systems.map((system) => system.id));
    setSelectedSystemIds((previous) => {
      const remaining = previous.filter((id) => loadedIds.has(id));
      return remaining.length === previous.length ? previous : remaining;
    });
  }, [systems]);

  const handleRowSelection = (id: string, checked: boolean) => {
    if (loading || deletingRef.current) return;
    setSelectedSystemIds((previous) => {
      if (checked) {
        return previous.includes(id) ||
          previous.length >= MAX_BULK_DELETE_AI_SYSTEMS
          ? previous
          : [...previous, id];
      }
      return previous.filter((selectedId) => selectedId !== id);
    });
  };

  const handleHeaderSelection = () => {
    if (loading || deletingRef.current) return;

    const allowedSystems = isAdmin
      ? systems
      : systems.filter((system) =>
          system.owner.id === system.createdById
            ? user?.email === system.owner.email
            : false
        );

    setSelectedSystemIds((previous) =>
      previous.length > 0
        ? []
        : allowedSystems
            .slice(0, MAX_BULK_DELETE_AI_SYSTEMS)
            .map((system) => system.id)
    );
  };

  const handleBulkDelete = async () => {
    if (
      selectedSystemIds.length === 0 ||
      selectedSystemIds.length > MAX_BULK_DELETE_AI_SYSTEMS ||
      loading ||
      deletingRef.current
    )
      return;

    const payload: BulkDeleteAISystemsRequest = {
      ids: [...selectedSystemIds]
    };
    deletingRef.current = true;
    setIsDeleting(true);
    try {
      const response = await bulkDeleteAiSystems(payload);
      const deletedIds = new Set(response.deletedIds);
      setSelectedSystemIds((previous) =>
        previous.filter((id) => !deletedIds.has(id))
      );
      dispatch(
        fetchSystemsSuccess({
          items: systems.filter((system) => !deletedIds.has(system.id)),
          total: Math.max(0, total - response.deletedCount)
        })
      );
      dispatch(fetchSystemsStart());
      dispatch(setOffset(0));
      dispatch(fetchSummaryData(filters.regulationCode || undefined));
      dispatch(
        showToast({
          message: `${response.message} (${response.deletedCount} deleted)`,
          type: 'success'
        })
      );
      setShowBulkDeleteModal(false);
    } catch (error) {
      dispatch(
        showToast({
          message:
            error instanceof Error
              ? error.message
              : 'Failed to delete selected AI systems',
          type: 'error'
        })
      );
    } finally {
      deletingRef.current = false;
      setIsDeleting(false);
    }
  };

  const ownerOptions = useMemo(() => {
    const options = owners.map((owner: Owner) => ({
      value: owner.id,
      label:
        user?.email === owner.email
          ? 'me'
          : `${owner.firstName} ${owner.lastName}`
    }));

    // set 'me' as first option
    const meIndex = options.findIndex((option) => option.label === 'me');
    if (meIndex > 0) {
      const [meOption] = options.splice(meIndex, 1);
      options.unshift(meOption);
    }

    return options;
  }, [owners, user?.email]);

  // Entity/role and risk/classification filter options are regulation-scoped:
  // sourced from GET /ai-systems/filter-options for the selected regulation.
  // null (All regulations, or a dimension the backend does not scope) -> no
  // options; the filters themselves are hidden by showRegulationScopedFilters.
  const entityOptions = useMemo(
    () => filterOptions?.entities ?? [],
    [filterOptions]
  );

  const riskClassificationOptions = useMemo(
    () => filterOptions?.riskClassifications ?? [],
    [filterOptions]
  );

  // Load the regulation list once and select a default. /regulations returns one
  // row per published pack version; dedupeRegulationsByCode collapses them,
  // keeping the highest published version. A single regulation is always in
  // scope - there is no "All" option.
  useEffect(() => {
    let isActive = true;

    const FALLBACK_OPTIONS = [{ value: 'EU_AI_ACT', label: 'EU AI Act' }];

    const applyOptions = (options: { value: string; label: string }[]) => {
      setRegulationOptions(options);
      // Preserve a valid current selection (e.g. across in-app navigation);
      // otherwise default to the first regulation.
      const current = regulationCodeRef.current;
      const isValidCurrent = Boolean(
        current && options.some((option) => option.value === current)
      );
      if (!isValidCurrent) {
        dispatch(setRegulationFilter(options[0].value));
      }
      setRegulationsReady(true);
    };

    const loadRegulations = async () => {
      try {
        const regulations = await fetchRegulations();
        if (!isActive) return;
        const options = dedupeRegulationsByCode(regulations).map(
          (regulation) => ({
            value: regulation.regulationCode,
            label: regulation.regulationName
          })
        );
        applyOptions(options.length > 0 ? options : FALLBACK_OPTIONS);
      } catch (error) {
        console.error('Failed to fetch regulations:', error);
        if (!isActive) return;
        applyOptions(FALLBACK_OPTIONS);
      }
    };

    loadRegulations();

    return () => {
      isActive = false;
    };
  }, [dispatch]);

  // Fetch entity/risk filter options whenever the regulation changes. For "All
  // regulations" there is nothing to scope, so clear options without a request.
  useEffect(() => {
    if (!filters.regulationCode) {
      setFilterOptions(null);
      return;
    }

    let isActive = true;
    // Clear the previous regulation's options first so switching regulations
    // never briefly renders the prior regulation's entity/risk options.
    setFilterOptions(null);

    const loadFilterOptions = async () => {
      try {
        const options = await fetchAiSystemFilterOptions(filters.regulationCode);
        if (isActive) setFilterOptions(options);
      } catch (error) {
        console.error('Failed to fetch filter options:', error);
        if (isActive) setFilterOptions(null);
      }
    };

    loadFilterOptions();

    return () => {
      isActive = false;
    };
  }, [filters.regulationCode]);

  // Summary is scoped strictly by regulation. Gated on regulationsReady so it
  // does not fire an unscoped request before the default regulation is set;
  // afterwards it re-runs on each regulation change.
  useEffect(() => {
    if (!regulationsReady) return;
    dispatch(fetchSummaryData(filters.regulationCode || undefined));
  }, [dispatch, filters.regulationCode, regulationsReady]);

  useEffect(() => {
    const loadOrganizations = async () => {
      setOrganizationsLoading(true);
      try {
        const orgs = await fetchOrganizations();
        setOrganizationOptions(orgs.map((org) => ({ value: org, label: org })));
      } catch (error) {
        console.error('Failed to fetch organizations:', error);
        setOrganizationOptions([]);
      } finally {
        setOrganizationsLoading(false);
      }
    };

    loadOrganizations();
  }, []);

  // Only a superadmin sees more than one tenant, so nobody else needs the
  // filter — and the list would be a single redundant entry for an admin.
  useEffect(() => {
    if (!isSuperAdmin) return;

    const loadAccounts = async () => {
      setAccountsLoading(true);
      try {
        const accounts = await fetchAiSystemAccounts();
        setAccountOptions(
          accounts.map((account) => ({
            value: account.id,
            label: account.name
          }))
        );
      } catch (error) {
        console.error('Failed to fetch organizations:', error);
        setAccountOptions([]);
      } finally {
        setAccountsLoading(false);
      }
    };

    loadAccounts();
  }, [isSuperAdmin]);

  useEffect(() => {
    dispatch(setEnableStepper(false));
    dispatch(setDocumentationReqBody(initialDocumentationParams));
    dispatch(setDocuments({ items: [], total: 0 }));
    dispatch(resetSystemLogging());
    dispatch(resetRepository());
    dispatch(resetAIPowered());
  }, [dispatch]);

  useEffect(() => {
    if (!user?.id || isDeleting || !regulationsReady) return;

    let isActive = true;

    const fetchSystems = async () => {
      dispatch(fetchSystemsStart());
      try {
        const response = await fetchAISystems(
          buildAISystemsQuery({ filters, pagination, sorting })
        );
        if (!isActive) return;

        if (pagination.offset === 0) {
          dispatch(fetchSystemsSuccess(response));
        } else {
          dispatch(appendSystemsSuccess(response));
        }
      } catch (error: any) {
        if (!isActive) return;
        dispatch(fetchSystemsFailure(error.message));
      }
    };

    fetchSystems();

    return () => {
      isActive = false;
    };
    // `pagination` is depended on whole now that the query is built from it.
    // Immer keeps the object identity stable unless limit/offset actually
    // change, so this re-runs no more often than the two fields did.
  }, [
    dispatch,
    filters,
    pagination,
    sorting,
    user?.id,
    isDeleting,
    regulationsReady
  ]);

  const hasActiveFilters = useMemo(() => {
    return (
      (filters.regulationCode && filters.regulationCode.length > 0) ||
      (filters.entity && filters.entity.length > 0) ||
      (filters.complianceStatus && filters.complianceStatus.length > 0) ||
      (filters.riskClassification && filters.riskClassification.length > 0) ||
      (filters.owners && filters.owners.length > 0) ||
      (filters.organization && filters.organization.length > 0) ||
      (filters.search && filters.search.length > 0)
    );
  }, [filters]);

  const statsData = useMemo(() => {
    if (summaryData) {
      return mapSummaryToStatsSection(summaryData);
    }
    return {
      totalSystems: 0,
      complianceData: {
        compliant: 0,
        inProgress: 0,
        nonCompliant: 0
      }
    };
  }, [summaryData]);

  const handleSearchChange = (value: string) => {
    dispatch(setSearch(value));
  };

  const handleRegulationChange = (value: string) => {
    dispatch(setRegulationFilter(value));
  };

  const handleResetFilters = () => {
    dispatch(resetFilters());
  };

  const handleLoadMore = () => {
    if (!loading && !deletingRef.current && hasMore) {
      dispatch(setOffset(systems.length));
    }
  };

  const handleDelete = () => {
    const fetchSystems = async () => {
      dispatch(fetchSystemsStart());
      try {
        const response = await fetchAISystems(
          buildAISystemsQuery({ filters, pagination, sorting }, { offset: 0 })
        );
        dispatch(fetchSystemsSuccess(response));
        dispatch(fetchSummaryData(filters.regulationCode || undefined));
      } catch (error: any) {
        dispatch(fetchSystemsFailure(error.message));
      }
    };

    fetchSystems();
  };

  const handleSort = (sortKey: string) => {
    if (sorting.sortBy === sortKey) {
      const newOrder = sorting.order === 'asc' ? 'desc' : 'asc';
      dispatch(setSorting({ sortBy: sortKey, order: newOrder }));
    } else {
      dispatch(setSorting({ sortBy: sortKey, order: 'asc' }));
    }
  };

  const handleSystemSelect = async (id: string) => {
    // Find the selected system from the list and set it as current
    const selectedSystem = systems.find((system) => system.id === id);

    if (!selectedSystem) return;

    // Regulation-assessment systems (e.g. LL144) have no questionnaire draft to
    // continue. Route to the assessment page until classified, then to detail.
    // Checked before the bulk-import branch so bulk-imported LL144 rows go to the
    // assessment wizard instead of being forced through the questionnaire.
    if (selectedSystem.assessmentType === 'REGULATION_ASSESSMENT') {
      dispatch({
        type: 'aiSystems/fetchCurrentSystemSuccess',
        payload: selectedSystem
      });
      navigate(
        selectedSystem.riskClassification
          ? `/ai-systems/${id}`
          : `/ai-systems/${id}/ll144-assessment`
      );
      return;
    }

    if (
      selectedSystem.origin === 'BULK_IMPORT' &&
      selectedSystem.complianceStatus === 'INITIATED'
    ) {
      const basicInformation = {
        name: selectedSystem.name,
        version: selectedSystem.version,
        description: selectedSystem.description ?? '',
        organization: selectedSystem.organization ?? '',
        complianceContact: selectedSystem.complianceContact ?? ''
      };

      const hasEmptyValue = Object.values(basicInformation).some(
        (value) =>
          value === null ||
          value === undefined ||
          (typeof value === 'string' && value.trim() === '')
      );

      if (hasEmptyValue) {
        dispatch(setAiSystemId(selectedSystem.id));
        dispatch(setEnableStepper(true));
        dispatch(setShowPreStep(true));
        dispatch(setPreStepPart('basicInformation'));
        navigate(PATHS.QUESTIONNAIRE, { state: { basicInformation } });
      } else {
        const startAiSystemRequest: StartAiSystemRequest = {
          ...basicInformation,
          assessmentType: 'QUESTIONNAIRE',
          aiSystemId: selectedSystem.id
        };

        dispatch(setStartAiSystemBodyRequest(startAiSystemRequest));

        const response = await startAiSystem(startAiSystemRequest);

        dispatch(setStartAiSystemBodyResponse(response));
        dispatch(setEnableStepper(true));
        dispatch(setShowPreStep(true));
        dispatch(setPreStepPart('entityClassification'));
        navigate(PATHS.QUESTIONNAIRE);
      }
      return;
    }

    if (
      !selectedSystem.wizardCompleted &&
      selectedSystem.assessmentType === 'AI_POWERED'
    ) {
      dispatch(resetAIPowered());
      dispatch(setIsDraftLoaded(true));
      dispatch(setEnableStepper(true));
      dispatch(setShowPreStep(false));
      dispatch(setAiSystemId(id));
      dispatch(
        setStartAiSystemBodyRequest({
          name: selectedSystem.name,
          version: selectedSystem.version,
          description: selectedSystem.description ?? '',
          organization: selectedSystem.organization ?? '',
          complianceContact: selectedSystem.complianceContact ?? '',
          assessmentType: 'AI_POWERED'
        })
      );

      try {
        dispatch(fetchClassificationsStart());
        const classifications = await getAIClassifications(id, {
          limit: 1,
          offset: 0
        });
        dispatch(fetchClassificationsSuccess(classifications));
        navigate(`/questionnaire`);
      } catch (error: any) {
        dispatch(
          fetchClassificationsFailure(
            error instanceof Error
              ? error.message
              : 'Failed to get AI classifications'
          )
        );
      }

      return;
    }

    if (!selectedSystem.wizardCompleted) {
      try {
        const response = await continueAiSystemDraft(id);
        const questions = Array.isArray(response.questions)
          ? response.questions
          : response.questions
            ? [response.questions]
            : [];
        const currentQuestion = response.question || questions[0];
        // Only E1 belongs to pre-step entity classification.
        // Other STEP1 entity questions (e.g. E2) should render in Stepper.
        const isEntityResumeQuestion = currentQuestion?.id === 'E1';

        dispatch(setIsDraftLoaded(true));
        dispatch(setEnableStepper(true));
        dispatch(setQuestionnaireId(response.questionnaireId));
        dispatch(setAiSystemId(id));
        dispatch(updateStepBasedOnBackend(response.step));

        if (response.step === 'REPORT') {
          dispatch(setShowPreStep(false));
          dispatch(setStartAiSystemBodyResponse(null));
          dispatch(setReportData(response));
        } else if (isEntityResumeQuestion && currentQuestion) {
          dispatch(setShowPreStep(true));
          dispatch(setPreStepPart('entityClassification'));
          dispatch(setPreviousQuestions([]));
          dispatch(
            setStartAiSystemBodyResponse({
              ...response,
              aiSystemId: response.aiSystemId || id,
              question: currentQuestion
            })
          );
        } else {
          dispatch(setShowPreStep(false));
          dispatch(setStartAiSystemBodyResponse(null));
          dispatch(setPreviousQuestions(questions));
        }

        navigate(`/questionnaire`);
      } catch (error) {
        throw new Error('Failed to fetch ai system');
      }
    } else {
      dispatch({
        type: 'aiSystems/fetchCurrentSystemSuccess',
        payload: selectedSystem
      });
      navigate(`/ai-systems/${id}`);
    }
  };

  return (
    <div className="flex flex-col gap-4 w-full h-full">
      {!regulationsReady && (
        <div className="absolute top-1/2 left-1/2">
          <Loader />
        </div>
      )}

      {regulationsReady && !loading && systems.length === 0 && !hasActiveFilters && (
        <EmptyState
          icon={<img src={StarIcon} alt="AI Systems" className="w-12 h-12" />}
          title="No AI systems yet"
          description="Get started by adding your first AI system for compliance assessment."
          action={<AddAISystemAction menuAlign="center" />}
        />
      )}

      {regulationsReady && loading && systems.length === 0 && !hasActiveFilters && (
        <div className="absolute top-1/2 left-1/2">
          <Loader />
        </div>
      )}

      {regulationsReady && (systems.length > 0 || hasActiveFilters) && (
        <>
          <StatsSection
            totalSystems={statsData.totalSystems}
            complianceData={statsData.complianceData}
            regulationOptions={regulationOptions}
            selectedRegulation={filters.regulationCode ?? ''}
            onRegulationChange={handleRegulationChange}
          />
          <SearchSection
            searchValue={filters.search}
            onSearchChange={handleSearchChange}
            placeholder="Search by keyword"
            inputClassName="border-gray-200"
          />
          <FiltersSection
            selectedEntity={filters.entity || []}
            onEntityChange={(values: string[]) =>
              dispatch(setEntityFilter(values))
            }
            selectedComplianceStatus={filters.complianceStatus || []}
            onComplianceStatusChange={(values: string[]) =>
              dispatch(setComplianceFilter(values))
            }
            selectedRiskClassification={filters.riskClassification || []}
            onRiskClassificationChange={(values: string[]) =>
              dispatch(setRiskFilter(values))
            }
            selectedOwners={filters.owners || []}
            onOwnersChange={(values: string[]) =>
              dispatch(setOwnersFilter(values))
            }
            selectedOrganization={filters.organization || []}
            onOrganizationChange={(values: string[]) =>
              dispatch(setOrganizationFilter(values))
            }
            selectedAccount={filters.accountId || []}
            onAccountChange={
              isSuperAdmin
                ? (values: string[]) => dispatch(setAccountFilter(values))
                : undefined
            }
            onResetFilters={handleResetFilters}
            showRegulationScopedFilters={Boolean(filters.regulationCode)}
            entityOptions={entityOptions}
            riskClassificationOptions={riskClassificationOptions}
            ownerOptions={ownerOptions}
            ownersLoading={ownersLoading}
            organizationOptions={organizationOptions}
            organizationsLoading={organizationsLoading}
            accountOptions={accountOptions}
            accountsLoading={accountsLoading}
          />
          {selectedSystemIds.length > 0 && (
            <div className="flex items-center justify-end gap-2 h-[30px]">
              <span className="text-gray-900 text-xs leading-5">
                {selectedSystemIds.length} selected
              </span>
              <TextSecondaryButton
                disabled={
                  selectedSystemIds.length === 0 || loading || isDeleting
                }
                onClick={() => setShowBulkDeleteModal(true)}
              >
                {isDeleting ? 'Deleting...' : 'Delete Selected'}
              </TextSecondaryButton>
            </div>
          )}
          <div className="flex-1 w-full relative">
            <AISystemsTable
              aiSystems={systems}
              selectedSystemIds={selectedSystemIds}
              onRowSelection={handleRowSelection}
              onHeaderSelection={handleHeaderSelection}
              selectionDisabled={loading || isDeleting}
              onSelect={handleSystemSelect}
              loading={loading || isDeleting}
              hasMore={hasMore}
              onLoadMore={handleLoadMore}
              onDelete={handleDelete}
              sortBy={sorting.sortBy}
              sortOrder={sorting.order}
              onSort={handleSort}
              allSystemSelected={allSystemsSeleceted}
            />
          </div>
          <ModalDeleteConfirmation
            open={showBulkDeleteModal}
            onClose={() => setShowBulkDeleteModal(false)}
            onConfirm={handleBulkDelete}
            title={`Delete AI ${isOneSelectedSystem ? 'System' : 'Systems'}?`}
            boldedDescription={`Are you sure you want to delete ${
              isOneSelectedSystem ? '' : selectedSystemIds.length
            } AI ${isOneSelectedSystem ? 'system' : 'systems'}? `}
            description="Deleting the AI systems will permanently remove all related data, including documents, requirements, risk assessments, repository connections, and monitoring logs. Any users assigned to these systems will also lose access. This action cannot be undone."
            isLoading={isDeleting}
            deleteButtonText={[
              `Delete AI ${isOneSelectedSystem ? 'System' : 'Systems'}`,
              'Deleting'
            ]}
          />
        </>
      )}
    </div>
  );
}
